import React, {
  createContext,
  CSSProperties,
  ReactNode,
  Ref,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactElement,
} from 'react'
import { usePopper } from 'react-popper'
import { Options } from '@popperjs/core'
import { Box, BoxOwnProps, Button, ButtonProps, BoxProps } from 'theme-ui'

type ContextType = {
  visible: boolean
  referenceRef: Ref<any>
  onDropdownClick(): void
  styles: null | { [k: string]: CSSProperties }
}

const DropdownContext = createContext<ContextType>({
  visible: false,
  styles: null,
  referenceRef: null,
  onDropdownClick: () => {},
})

interface DropdownProps extends Partial<Options> {
  children: ReactNode
}

function getDropdownButton(child: any) {
  return (
    child?.type &&
    [child.type.name, child.type.displayName].includes('DropdownButton')
  )
}

function Dropdown({ children, ...props }: DropdownProps) {
  const [visible, setVisibility] = useState(false)

  const referenceRef = useRef<any>(null)
  const popperRef = useRef(null)
  const ButtonComp =
    children instanceof Array ? children.find(getDropdownButton) : <Button />
  const childrens =
    children instanceof Array
      ? children.filter((child) => !getDropdownButton(child))
      : children

  const { styles, attributes } = usePopper(
    referenceRef.current,
    popperRef.current,
    {
      placement: 'bottom',
      modifiers: [
        {
          name: 'offset',
          enabled: true,
          options: {
            offset: [0, 10],
          },
        },
      ],
      ...props,
    }
  )

  useEffect(() => {
    // listen for clicks and close dropdown on body
    document.addEventListener('click', handleDocumentClick)
    return () => {
      document.removeEventListener('click', handleDocumentClick)
    }
  }, [])

  function handleDocumentClick(event: any) {
    if (referenceRef?.current?.contains(event?.target)) {
      return
    }
    setVisibility(false)
  }
  function handleDropdownClick() {
    setVisibility(!visible)
  }

  return (
    <DropdownContext.Provider
      value={{
        visible,
        styles,
        referenceRef,
        onDropdownClick: handleDropdownClick,
      }}
    >
      <React.Fragment>
        {ButtonComp}
        <div ref={popperRef} style={styles.popper} {...attributes.popper}>
          {childrens}
        </div>
      </React.Fragment>
    </DropdownContext.Provider>
  )
}

interface DropdownButtonProps extends ButtonProps {
  children?: ReactNode
  renderButton?(
    ref: ContextType['referenceRef'],
    onClick: ContextType['onDropdownClick']
  ): ReactElement
  sx?: ButtonProps['sx']
}

export const DropdownButton = ({
  children,
  renderButton,
  sx,
  ...props
}: DropdownButtonProps) => {
  const { referenceRef, onDropdownClick } = useContext(DropdownContext)

  if (renderButton) return renderButton(referenceRef, onDropdownClick)

  return (
    <Button
      type="button"
      ref={referenceRef}
      onClick={onDropdownClick}
      sx={sx}
      {...props}
    >
      {children}
    </Button>
  )
}

DropdownButton.displayName = 'DropdownButton'

export const DropdownList = ({
  children,
  sx,
}: {
  children: ReactNode
  sx?: BoxOwnProps['sx']
}) => {
  const { visible, styles } = useContext(DropdownContext)

  return (
    <Box
      sx={{
        display: visible ? 'flex' : 'none',
        width: 'max-content',
        flexDirection: 'column',
        backgroundColor: 'white',
        borderRadius: 'm',
        boxShadow: '0 0 8px 0 rgba(0,0,0,.14)',
        ...(styles?.offset as BoxOwnProps['sx']),
        ...sx,
      }}
    >
      {children}
    </Box>
  )
}

interface DropdownMenuProps extends BoxProps {
  children: ReactNode
  sx?: BoxOwnProps['sx']
}

export const DropdownMenu = ({ children, sx, ...props }: DropdownMenuProps) => {
  function handleClick(e) {
    e.stopPropagation()
    props.onClick?.(e)
  }

  return (
    <Box
      sx={{
        justifyContent: 'flex-start',
        height: 40,
        paddingX: '3',
        paddingY: '2',
        alignItems: 'center',
        ':hover': {
          bg: 'gray1',
        },
        ...sx,
      }}
      {...props}
      onClick={handleClick}
    >
      {children}
    </Box>
  )
}

export default Dropdown
